/* import __COLOCATED_TEMPLATE__ from './fly-multi-toggle.hbs.js'; */
var _class;
function _defineProperty(obj, key, value) { key = _toPropertyKey(key); if (key in obj) { Object.defineProperty(obj, key, { value: value, enumerable: true, configurable: true, writable: true }); } else { obj[key] = value; } return obj; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : String(i); }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _applyDecoratedDescriptor(target, property, decorators, descriptor, context) { var desc = {}; Object.keys(descriptor).forEach(function (key) { desc[key] = descriptor[key]; }); desc.enumerable = !!desc.enumerable; desc.configurable = !!desc.configurable; if ('value' in desc || desc.initializer) { desc.writable = true; } desc = decorators.slice().reverse().reduce(function (desc, decorator) { return decorator(target, property, desc) || desc; }, desc); if (context && desc.initializer !== void 0) { desc.value = desc.initializer ? desc.initializer.call(context) : void 0; desc.initializer = undefined; } if (desc.initializer === void 0) { Object.defineProperty(target, property, desc); desc = null; } return desc; }
import Component from '@glimmer/component';
import { action } from '@ember/object';
import { guidFor } from '@ember/object/internals';
import { GRAY_900 } from '@customerio/fly-components/utils/colors';
const INDICATOR_SELECTOR = '.js-fly-multi-toggle-select-indicator';
const INDICATOR_VELOCITY = 600;
const LABEL_TRANSITION_DURATION = 0.05;
const LABEL_TRANSITION_DELAY_MODIFIER = 0.8;
const CONSTRUCTOR_ERROR_MESSAGE = 'Either ariaLabel or label must be present';

/**
  FlyMultiToggle is essentially a nice-looking wrapper for what basically is a group of radio inputs.
  As radio inputs do, it requires a `name` field, which will be applied to the radio nodes (they're hidden through CSS, but are still present in the DOM).
  It also expects `value` as the current value, `onChange` which triggers when the value changes, and, naturally, the list of `options`.

  ```hbs
  <FlyMultiToggle
    \@name="action_type"
    \@value={{this.actionType}}
    \@options={{this.actionTypes}}
    \@onChange={{fn (mut this.actionType)}}
  />
  ```

  @class FlyMultiToggle
  @public
 */
let FlyMultiToggle = (_class = class FlyMultiToggle extends Component {
  constructor(owner, args) {
    super(owner, args);
    _defineProperty(this, "element", void 0);
    _defineProperty(this, "selectedLabel", void 0);
    // ResizeObserver doesn't have type definitions yet
    // https://github.com/microsoft/TypeScript/issues/37861
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    _defineProperty(this, "resizeObserver", void 0);
    _defineProperty(this, "componentId", guidFor(this));
    args.options.forEach(option => {
      if (option.ariaLabel == null && option.label == null) {
        throw new Error(CONSTRUCTOR_ERROR_MESSAGE);
      }
    });
  }
  get showLegend() {
    return this.args.showLegend ?? false;
  }
  addTransitionStyles() {
    let indicator = this.element.querySelector(INDICATOR_SELECTOR);
    indicator.style.transition = 'transform 0s ease-in-out, width 0s ease-in-out';
  }
  onInputClicked(event) {
    if (this.args.disabled) {
      return;
    }
    let clickedValue = event.target.value;
    if (clickedValue === this.args.value) {
      return;
    }
    this.args.onChange(clickedValue);
    this.positionSelectedIndicator();
  }
  positionSelectedIndicator() {
    let checkedInput = Array.from(this.element.querySelectorAll('input')).find(input => input.checked);
    if (!checkedInput) {
      return;
    }

    // We can safely use ! on the parentNode, as we know from the structure of
    // this component's template that the parent node is guaranteed to exist.
    let checkedLabel = checkedInput.parentNode.querySelector('label');
    let labelRect = checkedLabel.getBoundingClientRect();

    // We need to set the transform duration dynamically depending on the
    // distance between the previous and new selected options to make sure the
    // selected indicator maintains a constant velocity.
    // If we don't do this and set a constant transition duration, the
    // indicator would move considerably faster between distant options
    // compared to close options, which doesn't make for a great experience.
    let previousOffsetLeft = this.selectedLabel?.getBoundingClientRect().left ?? 0;
    let newOffsetLeft = labelRect.left ?? 0;
    let offsetDifference = Math.abs(newOffsetLeft - previousOffsetLeft);
    let transitionDuration = offsetDifference / INDICATOR_VELOCITY;
    let indicator = this.element.querySelector(INDICATOR_SELECTOR);
    if (this.selectedLabel) {
      this.selectedLabel.style.transition = '';
      this.selectedLabel.style.color = '';
      this.selectedLabel.style.fontWeight = '';
      if (transitionDuration !== 0) {
        indicator.style.transitionDuration = `${transitionDuration}s, ${transitionDuration}s`;
      }
      // We want the color of the label's text to smoothly transition into the
      // "checked" style only around the time when the indicator (above) is
      // sliding under it. If we were to trigger the color change instantly,
      // there may be short periods of time where the label's color has
      // transition to white without the purple indicator having moved
      // underneath yet - which isn't great, since the background color is white
      // as well.
      // The transition delay is based on the transition duration of the
      // indicator, multiplied by a less-than-1 modifier, so that the color
      // change triggers around the time the indicator gets to the position of
      // the label. The values have been chosen experimentally as what
      // felt/looked decent.
      checkedLabel.style.transition = `color ${LABEL_TRANSITION_DURATION}s ease-in-out ${transitionDuration * LABEL_TRANSITION_DELAY_MODIFIER}s, font-weight ${LABEL_TRANSITION_DURATION}s ease-in-out ${transitionDuration * LABEL_TRANSITION_DELAY_MODIFIER}s`;
    }

    // We set these styles via JS rather than CSS, because we need tighter
    // control over when the CSS transitions happen on the label's text.
    // If we set the color via CSS, the transitions we set via JS will be
    // ignored or not work as expected, as the :checked - based CSS classes will
    // trigger before JS sets the transitions.
    checkedLabel.style.color = GRAY_900;
    checkedLabel.style.fontWeight = '600';
    let width = labelRect?.width ?? 0;
    let containerRect = this.element.getBoundingClientRect();
    let leftPosition = Math.abs(containerRect.left - newOffsetLeft) - 1;
    if (this.args.size === 'sm') {
      width = width - 2;
      leftPosition = leftPosition + 1;
    }

    // Animating left and width is known to be bad for performance, because it
    // triggers reflow calculations, which could be bad.
    // Ideally we'd simply use translate transforms, but those can get slightly
    // blurry when the distances end up with fractional pixels. It's a detail
    // that I noticed and can't unsee.
    // Translating via a left absolute position doesn't seem to have the
    // problem, so I'd prefer to have a better-looking component at the cost of
    // performance.
    indicator.style.left = `${leftPosition}px`;
    indicator.style.width = `${width}px`;
    indicator.style.visibility = 'visible';
    indicator.style.opacity = '1';
    this.selectedLabel = checkedLabel;
  }
  registerElement(element) {
    this.element = element;
    this.positionSelectedIndicator();
    this.setupResizeObserver();
  }
  setupResizeObserver() {
    // @ts-ignore ResizeObserver doesn't have type definitions yet
    this.resizeObserver = new ResizeObserver(this.positionSelectedIndicator);
    this.resizeObserver.observe(this.element);
  }
  willDestroy() {
    super.willDestroy();
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
  }
}, (_applyDecoratedDescriptor(_class.prototype, "addTransitionStyles", [action], Object.getOwnPropertyDescriptor(_class.prototype, "addTransitionStyles"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "onInputClicked", [action], Object.getOwnPropertyDescriptor(_class.prototype, "onInputClicked"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "positionSelectedIndicator", [action], Object.getOwnPropertyDescriptor(_class.prototype, "positionSelectedIndicator"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "registerElement", [action], Object.getOwnPropertyDescriptor(_class.prototype, "registerElement"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "setupResizeObserver", [action], Object.getOwnPropertyDescriptor(_class.prototype, "setupResizeObserver"), _class.prototype)), _class);
export { FlyMultiToggle as default };